import { Capacitor } from "@capacitor/core";
import { PMTiles } from "pmtiles";

const ALLOWED_FILES = new Set([
  "portugal-lite.pmtiles",
  "portugal-pro.pmtiles",
  "portugal-contours.pmtiles",
]);
const IMMUTABLE_CACHE_CONTROL = "public, max-age=31536000, immutable";
const MAX_RANGE_CACHE_BYTES = 12 * 1024 * 1024;
const MAX_SINGLE_RANGE_CACHE_BYTES = 2 * 1024 * 1024;

let requestSeq = 0;
let rangeCacheBytes = 0;
const rangeCache = new Map();

export function clearNativePmtilesRangeCache() {
  rangeCache.clear();
  rangeCacheBytes = 0;
}

function rangeCacheKey(fileName, offset, length) {
  return `${fileName}:${offset}:${length}`;
}

function cloneArrayBuffer(buffer) {
  return buffer.slice(0);
}

function getCachedRange(fileName, offset, length) {
  const key = rangeCacheKey(fileName, offset, length);
  const entry = rangeCache.get(key);
  if (!entry) return null;
  rangeCache.delete(key);
  rangeCache.set(key, entry);
  return {
    data: cloneArrayBuffer(entry.data),
    cacheControl: IMMUTABLE_CACHE_CONTROL,
  };
}

function putCachedRange(fileName, offset, length, data) {
  const bytes = Number(data?.byteLength || 0);
  if (bytes <= 0 || bytes > MAX_SINGLE_RANGE_CACHE_BYTES) return;
  const key = rangeCacheKey(fileName, offset, length);
  const existing = rangeCache.get(key);
  if (existing) {
    rangeCacheBytes -= existing.bytes;
    rangeCache.delete(key);
  }
  rangeCache.set(key, {
    data: cloneArrayBuffer(data),
    bytes,
  });
  rangeCacheBytes += bytes;
  while (rangeCacheBytes > MAX_RANGE_CACHE_BYTES) {
    const oldestKey = rangeCache.keys().next().value;
    if (!oldestKey) break;
    const oldest = rangeCache.get(oldestKey);
    rangeCacheBytes -= Number(oldest?.bytes || 0);
    rangeCache.delete(oldestKey);
  }
}

function base64ToArrayBuffer(value) {
  const binary = atob(String(value || ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function readIosRange(fileName, offset, length, signal) {
  return new Promise((resolve, reject) => {
    const requestId = `map-range-${Date.now()}-${++requestSeq}`;
    let timeout = null;

    const cleanup = () => {
      window.removeEventListener("native:mapRange", onResult);
      signal?.removeEventListener?.("abort", onAbort);
      if (timeout) window.clearTimeout(timeout);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const onResult = (event) => {
      const detail = event?.detail || {};
      if (detail.requestId !== requestId) return;
      cleanup();
      if (detail.status !== "ok") {
        reject(new Error(String(detail.message || "native_map_range_failed")));
        return;
      }
      resolve({
        data: base64ToArrayBuffer(detail.base64),
        cacheControl: IMMUTABLE_CACHE_CONTROL,
      });
    };

    window.addEventListener("native:mapRange", onResult);
    signal?.addEventListener?.("abort", onAbort, { once: true });
    timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("native_map_range_timeout"));
    }, 30000);

    window.webkit.messageHandlers.nativeBridge.postMessage({
      action: "readMapRange",
      requestId,
      fileName,
      offset,
      length,
    });
  });
}

async function readAndroidRange(fileName, offset, length) {
  const plugin = window?.Capacitor?.Plugins?.GPSNative;
  if (!plugin?.readMapRange) {
    throw new Error("native_map_range_plugin_unavailable");
  }
  const result = await plugin.readMapRange({ fileName, offset, length });
  return {
    data: base64ToArrayBuffer(result?.base64),
    cacheControl: IMMUTABLE_CACHE_CONTROL,
  };
}

function normalizeNativeStat(fileName, result = {}) {
  const rawBytes = Number(
    result?.fileBytes ?? result?.fileSize ?? result?.size ?? result?.bytes ?? 0
  );
  const fileBytes = Number.isFinite(rawBytes) && rawBytes > 0 ? rawBytes : 0;
  const exists = result?.exists === true || result?.ok === true || fileBytes > 0;
  return {
    ok: exists,
    exists,
    fileName,
    fileBytes,
    fileSize: fileBytes,
    localUrl: String(result?.localUrl || ""),
  };
}

function readIosStat(fileName, signal) {
  return new Promise((resolve, reject) => {
    const requestId = `map-stat-${Date.now()}-${++requestSeq}`;
    let timeout = null;

    const cleanup = () => {
      window.removeEventListener("native:mapStat", onResult);
      signal?.removeEventListener?.("abort", onAbort);
      if (timeout) window.clearTimeout(timeout);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const onResult = (event) => {
      const detail = event?.detail || {};
      if (detail.requestId !== requestId) return;
      cleanup();
      if (detail.status && detail.status !== "ok") {
        reject(new Error(String(detail.message || "native_map_stat_failed")));
        return;
      }
      resolve(normalizeNativeStat(fileName, detail));
    };

    window.addEventListener("native:mapStat", onResult);
    signal?.addEventListener?.("abort", onAbort, { once: true });
    timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("native_map_stat_timeout"));
    }, 8000);

    window.webkit.messageHandlers.nativeBridge.postMessage({
      action: "statMapFile",
      requestId,
      fileName,
    });
  });
}

async function readAndroidStat(fileName) {
  const plugin = window?.Capacitor?.Plugins?.GPSNative;
  if (!plugin?.statMapFile) {
    throw new Error("native_map_stat_plugin_unavailable");
  }
  const result = await plugin.statMapFile({ fileName });
  return normalizeNativeStat(fileName, result);
}

export async function statNativePmtiles(fileName, signal) {
  if (!ALLOWED_FILES.has(fileName)) {
    throw new Error(`native_map_file_not_allowed:${fileName}`);
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const hasIosBridge =
    !!window?.webkit?.messageHandlers?.nativeBridge?.postMessage;
  const platform = Capacitor?.getPlatform?.();
  if ((platform === "ios" || hasIosBridge) && hasIosBridge) {
    return readIosStat(fileName, signal);
  }
  if (platform === "android") {
    return readAndroidStat(fileName);
  }
  throw new Error("native_map_stat_platform_unsupported");
}

export class NativePmtilesSource {
  constructor(fileName) {
    if (!ALLOWED_FILES.has(fileName)) {
      throw new Error(`native_map_file_not_allowed:${fileName}`);
    }
    this.fileName = fileName;
    this.key = `native-map://${fileName}`;
  }

  getKey() {
    return this.key;
  }

  async getBytes(offset, length, signal) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const safeOffset = Number(offset);
    const safeLength = Number(length);
    if (
      !Number.isSafeInteger(safeOffset) ||
      safeOffset < 0 ||
      !Number.isSafeInteger(safeLength) ||
      safeLength <= 0 ||
      safeLength > 8 * 1024 * 1024
    ) {
      throw new Error("native_map_range_invalid");
    }

    const cached = getCachedRange(this.fileName, safeOffset, safeLength);
    if (cached) return cached;

    const hasIosBridge =
      !!window?.webkit?.messageHandlers?.nativeBridge?.postMessage;
    const platform = Capacitor?.getPlatform?.();
    let result = null;
    if (
      (platform === "ios" || hasIosBridge) &&
      hasIosBridge
    ) {
      result = await readIosRange(this.fileName, safeOffset, safeLength, signal);
    } else if (platform === "android") {
      result = await readAndroidRange(this.fileName, safeOffset, safeLength);
    } else {
      throw new Error("native_map_range_platform_unsupported");
    }
    if (result?.data) putCachedRange(this.fileName, safeOffset, safeLength, result.data);
    return {
      ...result,
      cacheControl: IMMUTABLE_CACHE_CONTROL,
    };
  }
}

function lonToTileX(lon, zoom) {
  return Math.floor(((lon + 180) / 360) * 2 ** zoom);
}

function latToTileY(lat, zoom) {
  const bounded = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const rad = (bounded * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.asinh(Math.tan(rad)) / Math.PI) / 2) * 2 ** zoom
  );
}

const QUICK_PROBE_POINTS = [
  { lon: -8.42, lat: 41.55 }, // Braga
  { lon: -7.5, lat: 39.82 }, // Castelo Branco
  { lon: -8.89, lat: 38.52 }, // Setubal
];

const MIN_USABLE_VECTOR_TILE_BYTES = 512;
const PORTUGAL_PMTILES_TARGET_BOUNDS = [-10.1, 36.7, -5.8, 43.95];

function headerCoversTarget(header, tolerance = 0.05) {
  return (
    Number(header?.minLon) <= PORTUGAL_PMTILES_TARGET_BOUNDS[0] + tolerance &&
    Number(header?.minLat) <= PORTUGAL_PMTILES_TARGET_BOUNDS[1] + tolerance &&
    Number(header?.maxLon) >= PORTUGAL_PMTILES_TARGET_BOUNDS[2] - tolerance &&
    Number(header?.maxLat) >= PORTUGAL_PMTILES_TARGET_BOUNDS[3] - tolerance
  );
}

function headerBoundsAreReasonable(header) {
  const width = Number(header?.maxLon) - Number(header?.minLon);
  const height = Number(header?.maxLat) - Number(header?.minLat);
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 && width <= 8.5 && height <= 8.5;
}

function nativeVerifyConfig(fileName, header) {
  const maxZoom = Number(header?.maxZoom || 0);
  if (String(fileName).includes("pro")) {
    return {
      requiredMaxZoom: 15,
      zooms: [6, 10, 12, 14, 15].filter((z) => z <= maxZoom),
      minTileBytes: 256,
    };
  }
  if (String(fileName).includes("contours")) {
    return {
      requiredMaxZoom: 11,
      zooms: [6, 9, 10, 11].filter((z) => z <= maxZoom),
      minTileBytes: 1,
    };
  }
  return {
    requiredMaxZoom: 12,
    zooms: [6, 10, 12].filter((z) => z <= maxZoom),
    minTileBytes: 128,
  };
}

export async function verifyNativePmtiles(fileName) {
  const archive = new PMTiles(new NativePmtilesSource(fileName));
  const header = await archive.getHeader();
  if (
    !Number.isFinite(header?.minZoom) ||
    !Number.isFinite(header?.maxZoom) ||
    header.maxZoom < header.minZoom
  ) {
    throw new Error(`native_map_header_invalid:${fileName}`);
  }
  const cfg = nativeVerifyConfig(fileName, header);
  if (header.minZoom > 6 || header.maxZoom < cfg.requiredMaxZoom) {
    throw new Error(
      `native_map_zoom_range_invalid:${fileName}:z${header.minZoom}-${header.maxZoom}`
    );
  }
  if (!headerCoversTarget(header) || !headerBoundsAreReasonable(header)) {
    throw new Error(
      `native_map_bounds_invalid:${fileName}:${header.minLon},${header.minLat},${header.maxLon},${header.maxLat}`
    );
  }

  let largestTile = null;
  const failures = [];
  const probeZooms = [...new Set([cfg.zooms[0], cfg.zooms.at(-1)])];
  for (const zoom of probeZooms) {
    for (const point of QUICK_PROBE_POINTS) {
      const tile = await archive.getZxy(
        zoom,
        lonToTileX(point.lon, zoom),
        latToTileY(point.lat, zoom)
      );
      const tileBytes = Number(tile?.data?.byteLength || 0);
      if (!largestTile || tileBytes > largestTile.tileBytes) {
        largestTile = { tileZoom: zoom, tileBytes };
      }
      if (tileBytes < Math.min(MIN_USABLE_VECTOR_TILE_BYTES, cfg.minTileBytes)) {
        failures.push(`${zoom}:${point.lon},${point.lat}:${tileBytes}`);
      }
    }
  }
  if (failures.length) {
    throw new Error(
      `native_map_tile_probe_failed:${fileName}:largest=${largestTile?.tileBytes || 0}:missing=${failures.slice(0, 8).join("|")}`
    );
  }
  return {
    ok: true,
    minZoom: header.minZoom,
    maxZoom: header.maxZoom,
    tileZoom: largestTile?.tileZoom || header.minZoom,
    tileBytes: largestTile?.tileBytes || 0,
  };
}
